import type { ReactNode } from "react";
import type { Control, FieldPath, FieldValues, ControllerRenderProps } from "react-hook-form";
import { cn, Label } from "@fox1ck-org/ui";
import { useT } from "@/lib/i18n";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@fox1ck-org/ui/form";

/**
 * One labelled field of a drawer form, bound to react-hook-form.
 *
 * Not the package's `SettingRow`, which is a settings row with an `error`
 * string and no form binding; this composes the package's own `FormField` /
 * `FormItem` / `FormControl` from `@fox1ck-org/ui/form`, which are what wire a
 * field's id, its `aria-describedby` and its error text together.
 *
 * The drawers each grew their own `FieldRow`: a label, the control, and a
 * description. What none of them had was the fourth line — the error — because
 * with a useState per field there was nothing to render one from, and a failed
 * save arrived as a toast that did not say which field it meant.
 *
 * The package's `FormItem`/`FormLabel`/`FormControl` wire the id and
 * `aria-describedby` together, so a field cannot be labelled correctly and
 * described wrongly.
 */
export function RhfField<TValues extends FieldValues, TName extends FieldPath<TValues>>({
  control,
  name,
  label,
  required,
  optional,
  description,
  plain,
  children,
}: {
  control: Control<TValues>;
  name: TName;
  label: string;
  required?: boolean;
  optional?: boolean;
  description?: ReactNode;
  /**
   * The field's name as written (доска Setup: «Площадка», «Группа») instead
   * of the drawers' caps — the package's `Label textCase="none"` (0.51), for a
   * long form where twenty caps labels shout.
   */
  plain?: boolean;
  /** Receives the field's value and handlers; spread them onto the control. */
  children: (field: ControllerRenderProps<TValues, TName>) => ReactNode;
}) {
  const t = useT();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-1.5">
          {plain ? (
            <PlainLabel>
              <span>{label}</span>
              {required ? <span className="text-destructive">*</span> : null}
              {optional ? <span>{t("lib.common.optional")}</span> : null}
            </PlainLabel>
          ) : (
            <FormLabel className="flex items-center gap-1.5 text-xs font-medium text-primary">
              <span>{label}</span>
              {required ? <span className="text-destructive">*</span> : null}
              {optional ? (
                <span className="text-muted-foreground">{t("lib.common.optional")}</span>
              ) : null}
            </FormLabel>
          )}
          <FormControl>{children(field)}</FormControl>
          {description ? (
            <FormDescription className="text-xs">{description}</FormDescription>
          ) : null}
          <FormMessage className="text-xs" />
        </FormItem>
      )}
    />
  );
}

/**
 * The package's `Label textCase="none"` bound to the field the way `FormLabel`
 * binds its own: `htmlFor` the control, destructive while the field has an
 * error. `FormLabel` forwards to `Label` but does not type `textCase`.
 */
function PlainLabel({ children }: { children: ReactNode }) {
  const { error, formItemId } = useFormField();
  return (
    <Label htmlFor={formItemId} textCase="none" className={cn("flex items-center gap-1.5", error && "text-destructive")}>
      {children}
    </Label>
  );
}
